// agoraClientPool.js
'use strict';

import env from '../resource/env.js';
import { player as playerDebug } from '../resource/debug.js';

const log = playerDebug.extend('log:agoraClientPool');
const errorLog = playerDebug.extend('error:agoraClientPool');

/**
 * Pure data store for Agora client instances.
 * @type {Map<string, AgoraInstance>}
 * @key channelId
 * @value AgoraInstance:
 *   client: AgoraRTCClient | null
 *   loadScriptPromise: Promise | null
 *   createClientPromise: Promise | null
 *   joinChannelPromise: Promise | null
 *   waitForPublishingPromise: Promise | null
 *   subscribeTrackPromise: Promise | null
 *   joinChannelWithoutSubscribingPromise: Promise | null
 *   joinChannelWithSubscribingPromise: Promise | null
 *   publishedResolve: Function | null
 *   publishingTimeoutId: number | null
 *   publishedTracks: { video?, audio? }
 *   isActive: boolean
 *   isNearBy: boolean
 */
const agoraClientPool = new Map();

const DEFAULT_POOL_SIZE_MAX = 1;
let POOL_SIZE_MAX = Number(env.AGORA_POOL_SIZE_MAX) || DEFAULT_POOL_SIZE_MAX;

export const setPoolSizeMax = ({ size }) => {
  if (typeof size === 'number' && size > 0) {
    POOL_SIZE_MAX = size;
    log('setPoolSizeMax', { size });
  }
};

if (typeof window !== 'undefined') {
  window.__agoraClientPool = agoraClientPool;
}

/** Read-only lookup. Returns undefined if not found. */
const getAgoraInstance = ({ channelId }) => agoraClientPool.get(channelId);

/** Get or create — auto-creates a new instance (with pool cleanup) if not found. */
const getOrCreateAgoraInstance = ({ channelId }) => {
  if (!agoraClientPool.has(channelId)) {
    checkAgoraPoolSizeAndCleanup();
    agoraClientPool.set(channelId, {
      client: null,
      loadScriptPromise: null,
      createClientPromise: null,
      joinChannelPromise: null,
      waitForPublishingPromise: null,
      subscribeTrackPromise: null,
      joinChannelWithoutSubscribingPromise: null,
      joinChannelWithSubscribingPromise: null,
      publishedResolve: null,
      publishingTimeoutId: null,
      publishedTracks: {},
      isActive: false,
      isNearBy: false,
    });
  }
  return agoraClientPool.get(channelId);
};

const deleteAgoraInstance = ({ channelId }) => {
  agoraClientPool.delete(channelId);
};

/**
 * Partial update — only update fields that are explicitly passed.
 * Player manages isActive, Preloader manages isNearBy.
 */
const setAgoraInstanceStatus = ({ channelId, isActive, isNearBy } = {}) => {
  const agoraInstance = agoraClientPool.get(channelId);
  if (!agoraInstance) {
    errorLog('setAgoraInstanceStatus fail, instance not found', { channelId });
    return;
  }
  if (isActive !== undefined) agoraInstance.isActive = isActive;
  if (isNearBy !== undefined) agoraInstance.isNearBy = isNearBy;
  log('setAgoraInstanceStatus', {
    channelId,
    isActive: agoraInstance.isActive,
    isNearBy: agoraInstance.isNearBy,
  });
};

const getInactiveChannelIds = () => {
  const channelIds = [];
  agoraClientPool.forEach((agoraInstance, channelId) => {
    if (!agoraInstance.isActive && !agoraInstance.isNearBy) {
      channelIds.push(channelId);
    }
  });
  return channelIds;
};

const getIsAgoraClientPoolFull = () => agoraClientPool.size >= POOL_SIZE_MAX;

const pendingLeaves = new Map();
const LEAVE_TIMEOUT_MS = 1500;

const waitAgoraChannelLeave = async ({ channelId }) => {
  if (pendingLeaves.has(channelId)) {
    const leavePromise = pendingLeaves.get(channelId);
    log('waitAgoraChannelLeave: waiting', { channelId });

    let isTimeout = true;
    await Promise.race([
      leavePromise.then(() => {
        isTimeout = false;
      }),
      new Promise(resolve => setTimeout(resolve, LEAVE_TIMEOUT_MS)),
    ]);

    if (isTimeout && pendingLeaves.get(channelId) === leavePromise) {
      errorLog('waitAgoraChannelLeave: timeout, force cleanup', { channelId });
      pendingLeaves.delete(channelId);
    }

    log('waitAgoraChannelLeave: done', { channelId });
  }
};

const leaveAgoraChannel = ({ channelId }) => {
  const agoraClient = getAgoraInstance({ channelId })?.client;
  deleteAgoraInstance({ channelId });
  if (!agoraClient) {
    errorLog('leaveAgoraChannel: agora client not found', { channelId });
    return;
  }
  agoraClient.removeAllListeners();
  const leavePromise = agoraClient
    .leave()
    .catch(error => {
      errorLog('fail to leave channel', { channelId, error });
    })
    .finally(() => {
      pendingLeaves.delete(channelId);
    });
  pendingLeaves.set(channelId, leavePromise);
};

const checkAgoraPoolSizeAndCleanup = () => {
  if (!getIsAgoraClientPoolFull()) return;
  for (const channelId of getInactiveChannelIds()) {
    log('cleanup excess instance', { channelId });
    leaveAgoraChannel({ channelId });
    if (agoraClientPool.size < POOL_SIZE_MAX) break;
  }
};

const removeAllAgoraInstances = () => {
  for (const channelId of getAllChannelIds()) {
    log('cleanup all agora instances', { channelId });
    leaveAgoraChannel({ channelId });
  }
};

const getAllChannelIds = () => {
  return Array.from(agoraClientPool.keys());
};

export {
  getAgoraInstance,
  getOrCreateAgoraInstance,
  leaveAgoraChannel,
  removeAllAgoraInstances,
  setAgoraInstanceStatus,
  waitAgoraChannelLeave,
};
